import { useEffect } from 'react'
import { type ResultsStore } from 'pages/conversations/pages/search/ui/Results'
import { type ITabsResultsKeys } from 'pages/conversations/pages/search/ui/Results/store/types'

const TAB_ORDER: ITabsResultsKeys[] = ['top_results', 'contacts', 'messages']

type IUseResultsKeyboardNavigationParams = {
  resultsStore: ResultsStore
  focusSearchInput: () => void
  enabled: boolean
}

export const useResultsKeyboardNavigation = ({
  resultsStore,
  focusSearchInput,
  enabled,
}: IUseResultsKeyboardNavigationParams) => {
  const { keyboardNavStore } = resultsStore

  useEffect(() => {
    if (!enabled) return

    const handleKeyDown = (event: KeyboardEvent) => {
      const { isNavigating, highlightedIndex, moveDown, moveUp, setHighlightedIndex, reset } =
        keyboardNavStore
      const { currentTabItemCount, activeTab, setActiveTab, currentTabItems, handleGoToProfile } =
        resultsStore

      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault()
          moveDown(currentTabItemCount)
          break
        }
        case 'ArrowUp': {
          if (!isNavigating) return

          event.preventDefault()
          const result = moveUp()

          if (result === -1) {
            focusSearchInput()
          }
          break
        }
        case 'ArrowLeft': {
          if (!isNavigating) return

          event.preventDefault()
          const currentLeftIndex = TAB_ORDER.indexOf(activeTab)

          if (currentLeftIndex > 0) {
            setActiveTab(TAB_ORDER[currentLeftIndex - 1])
            setHighlightedIndex(0)
          }
          break
        }
        case 'ArrowRight': {
          if (!isNavigating) return

          event.preventDefault()
          const currentRightIndex = TAB_ORDER.indexOf(activeTab)

          if (currentRightIndex < TAB_ORDER.length - 1) {
            setActiveTab(TAB_ORDER[currentRightIndex + 1])
            setHighlightedIndex(0)
          }
          break
        }
        case 'Enter': {
          if (highlightedIndex === null) return

          event.preventDefault()
          const item = currentTabItems[highlightedIndex]

          if (item) {
            handleGoToProfile(item)
          }
          break
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [enabled, resultsStore, keyboardNavStore, focusSearchInput])
}
